import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';
import { Layout } from '../../components/Layout.js';
import { Seo } from '../../components/Seo.js';
import { MagicLinkForm } from './MagicLinkForm.js';

export function SignupPage() {
  const { t } = useTranslation('auth');
  const { lang } = useParams();
  const locale = (lang as 'es' | 'en') ?? 'es';

  const perks = [
    t('signup.perks.free_tickets'),
    t('signup.perks.live_fast'),
    t('signup.perks.direct_payouts'),
    t('signup.perks.qr_included'),
  ];

  return (
    <Layout>
      <Seo
        title={t('signup.seo_title')}
        description={t('signup.seo_description')}
        locale={locale}
      />
      <div className="max-w-5xl mx-auto px-4 pt-12 sm:pt-16 pb-16 grid gap-10 lg:grid-cols-[1.1fr_1fr] items-start">
        {/* Form column */}
        <div className="lg:order-2 max-w-md w-full mx-auto lg:mx-0">
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[var(--color-accent)] font-semibold mb-3 px-3 py-1 rounded-full border border-[var(--color-accent)]/30 bg-[var(--color-accent)]/5">
            {t('signup.plan_badge')}
          </span>
          <h1 className="text-3xl sm:text-4xl font-bold mb-2 tracking-tight">
            {t('signup.title')}
          </h1>
          <p className="text-neutral-400 mb-7 sm:mb-8">{t('signup.subtitle')}</p>
          <MagicLinkForm submitLabel={t('signup.submit')} intent="signup" />
          <p className="text-xs text-neutral-500 mt-4">
            {t('signup.legal_prefix')}
            <Link to={`/${lang}/legal/terms`} className="underline hover:text-neutral-300">
              {t('signup.legal_terms')}
            </Link>
            {t('signup.legal_and')}
            <Link to={`/${lang}/legal/privacy`} className="underline hover:text-neutral-300">
              {t('signup.legal_privacy')}
            </Link>
            {t('signup.legal_suffix')}
          </p>
          <p className="text-sm text-neutral-500 mt-6 text-center">
            {t('signup.already_account')}{' '}
            <Link to={`/${lang}/auth/login`} className="text-[var(--color-accent)] hover:underline">
              {t('login.title')}
            </Link>
          </p>
        </div>

        {/* Marketing column — perks, social proof, anti-piracy reassurance */}
        <aside className="lg:order-1 lg:sticky lg:top-24">
          <div className="card relative overflow-hidden">
            <div
              aria-hidden
              className="absolute -top-16 -left-10 w-48 h-48 rounded-full opacity-20 blur-3xl"
              style={{ background: 'var(--color-accent)' }}
            />
            <div className="relative">
              <p className="text-xs uppercase tracking-widest text-neutral-500 font-semibold mb-3">
                {t('signup.right_panel_eyebrow')}
              </p>
              <h2 className="text-xl sm:text-2xl font-bold leading-tight mb-5">
                {t('signup.right_panel_title')}
              </h2>
              <ul className="space-y-3 text-sm">
                {perks.map((p) => (
                  <li key={p} className="flex items-start gap-3">
                    <Check />
                    <span className="text-neutral-200">{p}</span>
                  </li>
                ))}
              </ul>
              <p className="text-xs text-neutral-500 mt-6 pt-6 border-t border-neutral-900">
                {t('signup.right_panel_footer')}
              </p>
            </div>
          </div>
        </aside>
      </div>
    </Layout>
  );
}

function Check() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="text-[var(--color-accent)] flex-shrink-0 mt-1"
      aria-hidden
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}
